import { toast } from "@/shared/hooks/useToast";
import { useEffect, useState } from "react";
import {
  fetchDataForwarderRetryEntries,
  resendDataForwarderMessages,
  sendManualJson,
} from "../api/dataForwarderRetry";
import type {
  DataForwarderRetryResultSummary,
  DataForwarderRetryRow,
} from "../types/recover-types";

export function useDataForwarderRetry() {
  const [rows, setRows] = useState<DataForwarderRetryRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<DataForwarderRetryResultSummary | null>(null);

  const reload = async () => {
    setIsLoading(true);
    try {
      const data = await fetchDataForwarderRetryEntries();
      setRows(data);
      setResult(null);
    } catch (error) {
      toast({
        title: "데이터 로드 실패",
        description: error instanceof Error ? error.message : "데이터를 불러오는 데 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 초기 로드
  useEffect(() => {
    void reload();
  }, []);

  const resendSelected = async (transIds: string[]) => {
    setIsLoading(true);
    try {
      const summary = await resendDataForwarderMessages(transIds);

      // 성공한 trans_id들의 상태를 retried로 업데이트
      if (summary.successTransIds.length > 0) {
        setRows((prev) =>
          prev.map((r) =>
            summary.successTransIds.includes(r.transId) ? { ...r, status: "retried" } : r
          )
        );
      }

      setResult(summary);

      toast({
        title: "재전송 완료",
        description: `성공: ${summary.successTransIds.length}건, 실패: ${summary.failedTransIds.length}건`,
      });
    } catch (error) {
      toast({
        title: "재전송 실패",
        description: error instanceof Error ? error.message : "재전송에 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendManualJsonHandler = async (jsonText: string) => {
    setIsLoading(true);
    try {
      let message: unknown;
      try {
        message = JSON.parse(jsonText);
      } catch {
        return { ok: false as const, error: "올바른 JSON 형식이 아닙니다." };
      }

      if (typeof message !== "object" || message === null || Array.isArray(message)) {
        return { ok: false as const, error: "JSON에 'trans_id' 필드가 없습니다." };
      }
      const parsed = message as Record<string, unknown>;
      if (!parsed.trans_id) {
        return { ok: false as const, error: "JSON에 'trans_id' 필드가 없습니다." };
      }

      const transId = String(parsed.trans_id);
      const exists = rows.some((r) => r.transId === transId);
      if (!exists) {
        return {
          ok: false as const,
          error: `data_forwarder_stats 테이블에 '${transId}' trans_id가 존재하지 않습니다.`,
        };
      }

      const summary = await sendManualJson(jsonText);

      // 상태 업데이트
      setRows((prev) => prev.map((r) => (r.transId === transId ? { ...r, status: "retried" } : r)));
      setResult(summary);

      toast({
        title: "수동 전송 성공",
        description: `'${transId}' 메시지가 성공적으로 전송되었습니다.`,
      });

      return { ok: true as const };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "전송에 실패했습니다.";
      return { ok: false as const, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    rows,
    isLoading,
    result,
    reload,
    resendSelected,
    sendManualJson: sendManualJsonHandler,
    clearResult: () => setResult(null),
  };
}
